import React, { useEffect, useState } from 'react';

import * as orgRoutes from 'src/app/account/routes';
import { usePrivateSubgraphSyncMutation } from 'src/hooks/useCloudResourceManagement';
import { useRouteParams } from 'src/hooks/useRouteParams';

import {
  ButtonState,
  NewSupergraphOnboardingControls,
} from '../NewSupergraphOnboardingControls';
import { NewSupergraphOnboardingStepLayout } from '../NewSupergraphOnboardingStepLayout';
import { AvailableSubgraphUrls } from '../selectDedicatedPlanStep/availableSubgraphUrls/AvailableSubgraphUrls';
import {
  CommonCloudDedicatedProps,
  CommonStepProps,
  SupergraphOnboardingStepPrivate,
} from '../shared';
import { RegisteredGraphTabContent } from '../yourGraphQLApiStep/RegisteredGraphTabContent';
import { UnregisteredGraphTabContent } from '../yourGraphQLApiStep/UnregisteredGraphTabContent';

import { OnboardingPrivateResourcesSection } from './OnboardingPrivateResourcesSection';

export const SetupEndpointorPrivateConnectionStep = ({
  setPreviousStep,
  previousStep,
  values,
  ...contentProps
}: React.ComponentProps<typeof SetupEndpointorPrivateConnectionStepContent> &
  Omit<
    React.ComponentProps<typeof NewSupergraphOnboardingStepLayout>,
    'description' | 'image' | 'children' | 'stepId'
  >) => {
  return (
    <NewSupergraphOnboardingStepLayout
      values={values}
      stepId={SupergraphOnboardingStepPrivate.AddAWSAccount}
      previousStep={previousStep}
      setPreviousStep={setPreviousStep}
      className="mx-auto w-3/5"
      hasPrivateSubgraphs={contentProps.hasPrivateSubgraphs}
    >
      <SetupEndpointorPrivateConnectionStepContent
        {...contentProps}
        values={values}
      />
    </NewSupergraphOnboardingStepLayout>
  );
};

const SetupEndpointorPrivateConnectionStepContent = ({
  availableUnregisteredResources,
  setAvailableUnregisteredResources,
  creatingSupergraph,
  continueAnalyticsLabelsByStep,
  values,
  errors,
  nextButtonState,
  setNextButtonState,
  setFieldValue,
  handleBlur,
  setHasPrivateSubgraphs,
  whenStepsBegan,
  setWhenStepsBegan,
  setShouldQueryPrivateSubgraphs,
  prefillGraphRef,
  isDedicatedPlan,
  flowType,
  selectedPrivateDomainName,
  setSelectedPrivateDomainName,
}: {
  loading?: boolean;
  nextButtonState: ButtonState;
  setNextButtonState: React.Dispatch<React.SetStateAction<ButtonState>>;
} & CommonStepProps &
  CommonCloudDedicatedProps &
  React.ComponentProps<typeof UnregisteredGraphTabContent> &
  React.ComponentProps<typeof RegisteredGraphTabContent>) => {
  const { includeVariantOnboardingStep } = useRouteParams(
    orgRoutes.supergraphOnboarding,
  );
  const isVariantOnboardingFlow = includeVariantOnboardingStep === 'true';

  const {
    sync,
    data: sharedResources,
    loading: sharedResourcesLoading,
    error: sharedResourcesError,
  } = usePrivateSubgraphSyncMutation();

  useEffect(() => {
    setWhenStepsBegan((prev) => ({
      ...(prev ?? {}),
      'Connect your private endpoint url': Date.now(),
    }));
  }, [setWhenStepsBegan]);

  useEffect(() => {
    setNextButtonState({
      isDisabled:
        values.arn.length < 1 ||
        !!errors.arn ||
        sharedResourcesLoading ||
        !!sharedResourcesError,
      disabledReason:
        values.arn.length < 1 ? 'Invalid ARN' : (errors.arn ?? undefined),
    });
  }, [
    availableUnregisteredResources,
    errors.arn,
    values.arn.length,
    sharedResourcesError,
    sharedResourcesLoading,
    setNextButtonState,
  ]);

  const [showAvailbleSubgraphsModal, setShowAvailableSubgraphsModal] =
    useState(false);

  useEffect(() => {
    if (prefillGraphRef) setShouldQueryPrivateSubgraphs(true);
  }, [prefillGraphRef, setShouldQueryPrivateSubgraphs]);

  useEffect(() => {
    if (availableUnregisteredResources.length > 0) {
      setHasPrivateSubgraphs(true);

      // If we're in the variant onboarding flow, we want to show the modal
      // during this check if they have any available subgraphs
      // so they can select one to use for their new variant
      if (isVariantOnboardingFlow) {
        setShowAvailableSubgraphsModal(true);
      }
    }
  }, [
    availableUnregisteredResources,
    isVariantOnboardingFlow,
    setHasPrivateSubgraphs,
  ]);

  return (
    <div>
      <div className="mb-2 text-2xl font-semibold">
        Link your Private Resource to your GraphOS Router
      </div>

      <OnboardingPrivateResourcesSection
        availableUnregisteredResources={availableUnregisteredResources}
        setAvailableUnregisteredResources={setAvailableUnregisteredResources}
        errors={errors}
        values={values}
        setFieldValue={setFieldValue}
        handleBlur={handleBlur}
        sync={sync}
        sharedResources={sharedResources}
        sharedResourcesError={sharedResourcesError}
        sharedResourcesLoading={sharedResourcesLoading}
      />

      <div className="hidden items-center justify-between px-0 py-16 3xl:flex">
        <NewSupergraphOnboardingControls.CancelButton
          creatingSupergraph={creatingSupergraph}
        />

        <NewSupergraphOnboardingControls.BackButton
          creatingSupergraph={creatingSupergraph}
          className="ml-auto"
        />
        <NewSupergraphOnboardingControls.NextButton
          whenStepsBegan={whenStepsBegan}
          smallScreen={false}
          creatingSupergraph={creatingSupergraph}
          state={nextButtonState}
          continueAnalyticsLabelsByStep={continueAnalyticsLabelsByStep}
        />
        <div>
          {availableUnregisteredResources.length > 0 && (
            <AvailableSubgraphUrls
              isOpen={showAvailbleSubgraphsModal}
              onClose={() => setShowAvailableSubgraphsModal(false)}
              availableUnregisteredResources={availableUnregisteredResources}
              values={values}
              setFieldValue={setFieldValue}
              flowType={flowType}
              isDedicatedPlan={isDedicatedPlan}
              isVariantOnboardingFlow={isVariantOnboardingFlow}
              selectedPrivateDomainName={selectedPrivateDomainName}
              setSelectedPrivateDomainName={setSelectedPrivateDomainName}
            />
          )}
        </div>
      </div>
    </div>
  );
};
